// ── Ubicación del navegador al fichar ──────────────────────────────────────
// La web pide la ubicación igual que la app cuando la empresa geolocaliza al
// trabajador (user.geoTracking): fichaje del botón, fichajes manuales y
// solicitudes de corrección. El formato que se guarda y la regla de bloqueo
// viven en src/lib/geoInfo.js (compartido con la app); aquí solo se habla con
// navigator.geolocation y se traducen sus errores.

import { geoCaptureMode } from '../lib/geoInfo';

export const BROWSER_GEO_OPTIONS = Object.freeze({
  enableHighAccuracy: true,
  timeout: 12000,     // ms que damos al navegador para fijar la posición
  maximumAge: 60000,  // aceptamos una lectura de hace ≤ 1 min
});

// Margen extra sobre `timeout`: si el usuario ignora (o cierra) el aviso de
// permiso, algunos navegadores (Firefox "Ahora no") NUNCA llaman a ningún
// callback. Sin este tope el botón se quedaría girando para siempre.
const GUARD_EXTRA_MS = 3000;

const HOW_TO_ALLOW =
  'Para permitirla, pulsa el icono del candado (o de ajustes) junto a la dirección web, activa «Ubicación» y vuelve a intentarlo.';

export const BROWSER_GEO_MESSAGES = Object.freeze({
  unsupported: 'Este navegador no permite obtener la ubicación. Usa un navegador actualizado (Chrome, Edge, Firefox o Safari) o ficha desde la app.',
  insecure: 'El navegador solo permite la ubicación en conexiones seguras (https://). Abre la web con https:// y vuelve a intentarlo.',
  denied: `No has dado permiso para usar tu ubicación en esta web. ${HOW_TO_ALLOW}`,
  unavailable: 'No se ha podido determinar tu ubicación. Comprueba que la ubicación del dispositivo está activada y que tienes conexión.',
  timeout: 'La ubicación ha tardado demasiado en responder. Inténtalo de nuevo en unos segundos.',
});

export class BrowserGeoError extends Error {
  constructor(code, message) {
    super(message || BROWSER_GEO_MESSAGES[code] || BROWSER_GEO_MESSAGES.unavailable);
    this.name = 'BrowserGeoError';
    this.code = code;
  }
}

// PositionError.code del navegador → nuestro código.
const CODE_FROM_POSITION_ERROR = { 1: 'denied', 2: 'unavailable', 3: 'timeout' };

// Posición actual del navegador → Promise<{ lat, lng, accuracy }>.
// Rechaza con BrowserGeoError (code: unsupported | insecure | denied |
// unavailable | timeout) y un mensaje en castellano listo para el usuario.
// `env` permite inyectar navigator/window en los tests.
export const getBrowserPosition = (options = {}, env = {}) => {
  const nav = env.navigator !== undefined ? env.navigator : (typeof navigator !== 'undefined' ? navigator : null);
  const win = env.window !== undefined ? env.window : (typeof window !== 'undefined' ? window : null);
  const opts = { ...BROWSER_GEO_OPTIONS, ...options };

  if (win && win.isSecureContext === false) return Promise.reject(new BrowserGeoError('insecure'));
  if (!nav || !nav.geolocation || typeof nav.geolocation.getCurrentPosition !== 'function') {
    return Promise.reject(new BrowserGeoError('unsupported'));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const guard = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new BrowserGeoError('timeout'));
    }, (Number(opts.timeout) || BROWSER_GEO_OPTIONS.timeout) + GUARD_EXTRA_MS);

    const done = (fn) => (arg) => {
      if (settled) return;
      settled = true;
      clearTimeout(guard);
      fn(arg);
    };

    try {
      nav.geolocation.getCurrentPosition(
        done((pos) => {
          const c = pos && pos.coords;
          const lat = Number(c && c.latitude);
          const lng = Number(c && c.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            reject(new BrowserGeoError('unavailable'));
            return;
          }
          const acc = Number(c.accuracy);
          resolve({ lat, lng, accuracy: Number.isFinite(acc) ? acc : null });
        }),
        done((err) => reject(new BrowserGeoError(CODE_FROM_POSITION_ERROR[err && err.code] || 'unavailable'))),
        {
          enableHighAccuracy: !!opts.enableHighAccuracy,
          timeout: opts.timeout,
          maximumAge: opts.maximumAge,
        }
      );
    } catch (_) {
      done(() => reject(new BrowserGeoError('unavailable')))();
    }
  });
};

// Modo de captura con el perfil MÁS RECIENTE del trabajador: si la empresa
// acaba de activarle la geolocalización, no hace falta recargar la web. Se
// pide la ficha (fetchMe = getMe de services/api) con un tope de `timeoutMs`;
// si no llega a tiempo, falla o es de otra persona, se usa `user` (sesión).
export const resolveGeoMode = async (user, fetchMe, { timeoutMs = 2500 } = {}) => {
  if (!user || typeof fetchMe !== 'function') return geoCaptureMode(user);
  let timer = null;
  try {
    const me = await Promise.race([
      Promise.resolve().then(fetchMe),
      new Promise((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); }),
    ]);
    if (me && !Array.isArray(me) && me.geoTracking != null &&Number(me.id) === Number(user.id)) {
      return geoCaptureMode(me);
    }
  } catch (_) {
    // red / backend sin /api/me → nos quedamos con la sesión
  } finally {
    if (timer) clearTimeout(timer);
  }
  return geoCaptureMode(user);
};
